import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Plus, UserRound } from "lucide-react";
import { api, ApiError, type Pilot } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, Input, PageTitle, Panel } from "../components/ui";

type Roster = Awaited<ReturnType<typeof api.listPilots>>;

/**
 * The people who fly. A pilot here is a record, not a login: it gives an
 * organisation one spelling of each name, and a way to see what each has flown.
 * Archiving hides a pilot from the list iPads offer but leaves every sheet they
 * flew exactly as printed.
 */
export function Pilots() {
  const auth = useAuth();
  const isAdmin = auth.status === "authenticated" && auth.me.user.role === "admin";

  const [roster, setRoster] = useState<Roster | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    try {
      setRoster(await api.listPilots());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load the pilots.");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /** Runs a change, then reloads; an API complaint becomes the page's message. */
  async function change(action: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
      return false;
    }
  }

  async function add(displayName: string) {
    setBusy(true);
    const ok = await change(() => api.createPilot(displayName));
    setBusy(false);
    return ok;
  }

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (await add(name)) setName("");
  }

  const live = roster?.pilots.filter((p) => !p.archivedAt) ?? [];
  const archived = roster?.pilots.filter((p) => p.archivedAt) ?? [];
  const detail = roster
    ? [`${live.length} pilot${live.length === 1 ? "" : "s"}`, archived.length ? `${archived.length} archived` : null]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  return (
    <div className="flex flex-col gap-8">
      <PageTitle detail={detail}>Pilots</PageTitle>

      {error && <Alert>{error}</Alert>}

      {isAdmin && (
        <form onSubmit={onAdd} className="flex gap-3 max-w-xl">
          <Input
            aria-label="Pilot's name"
            placeholder="Pilot's name"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-0"
          />
          <Button type="submit" busy={busy} disabled={!name.trim()}>
            <Plus size={19} strokeWidth={2.6} aria-hidden />
            Add pilot
          </Button>
        </form>
      )}

      {isAdmin && roster && roster.unlinkedNames.length > 0 && (
        <section>
          <p className="section-label mb-2.5 px-1">Names on missions, not on the list</p>
          <Panel>
            <ul className="divide-y divide-rule-soft">
              {roster.unlinkedNames.map((u) => (
                <li key={u.name} className="flex items-center gap-4 px-5 py-3.5">
                  <span className="flex-1 min-w-0 truncate text-[1rem] font-semibold text-ink">{u.name}</span>
                  <span className="text-[0.9rem] text-ink-soft tabular-nums">
                    {u.missionCount} mission{u.missionCount === 1 ? "" : "s"}
                  </span>
                  <Button variant="secondary" className="h-9 px-4" disabled={busy} onClick={() => void add(u.name)}>
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          </Panel>
          <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft">
            Adding a name links the missions already logged under it. What each sheet printed doesn't change.
          </p>
        </section>
      )}

      <Panel>
        {roster === null ? (
          <p className="p-6 text-[0.95rem] text-ink-soft">Loading…</p>
        ) : live.length === 0 ? (
          <div className="p-10 text-center">
            <span className="inline-grid place-items-center w-14 h-14 rounded-lg bg-accent/20 text-accent-text mb-4">
              <UserRound size={30} strokeWidth={2.2} aria-hidden />
            </span>
            <p className="text-[1.1rem] font-semibold text-ink mb-1.5">No pilots yet</p>
            <p className="text-[0.95rem] text-ink-soft max-w-sm mx-auto">
              {isAdmin
                ? "Add the people who fly. Missions already logged under their name are linked to them."
                : "An admin can add the people who fly."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {live.map((p) => (
              <PilotRow key={p.id} pilot={p} isAdmin={isAdmin} change={change} />
            ))}
          </ul>
        )}
      </Panel>

      {archived.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            aria-expanded={showArchived}
            className="px-1 text-[0.95rem] text-ink-soft hover:text-ink underline-offset-4 hover:underline"
          >
            {showArchived ? "Hide archived" : `Show archived (${archived.length})`}
          </button>
          {showArchived && (
            <Panel className="mt-2.5">
              <ul className="divide-y divide-rule-soft">
                {archived.map((p) => (
                  <PilotRow key={p.id} pilot={p} isAdmin={isAdmin} change={change} />
                ))}
              </ul>
            </Panel>
          )}
        </section>
      )}
    </div>
  );
}

function PilotRow({
  pilot, isAdmin, change,
}: { pilot: Pilot; isAdmin: boolean; change: (action: () => Promise<unknown>) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pilot.displayName);
  const archived = pilot.archivedAt !== null;

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim() || draft.trim() === pilot.displayName) return setEditing(false);
    if (await change(() => api.updatePilot(pilot.id, { displayName: draft }))) setEditing(false);
  }

  return (
    <li className="flex items-center gap-4 px-5 py-3.5 flex-wrap">
      {editing ? (
        <form onSubmit={save} className="flex flex-1 min-w-0 gap-2">
          <Input
            aria-label={`Rename ${pilot.displayName}`}
            value={draft}
            maxLength={100}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 min-w-0 h-10"
          />
          <Button type="submit" className="h-10 px-4">Save</Button>
          <Button type="button" variant="secondary" className="h-10 px-4" onClick={() => { setEditing(false); setDraft(pilot.displayName); }}>
            Cancel
          </Button>
        </form>
      ) : (
        <>
          <span className={`flex-1 min-w-0 truncate text-[1rem] font-semibold ${archived ? "text-ink-soft" : "text-ink"}`}>
            {pilot.displayName}
          </span>
          {pilot.missionCount > 0 ? (
            <Link
              to={`/app/missions?pilot=${pilot.id}`}
              className="text-[0.9rem] text-accent-text hover:underline underline-offset-4 tabular-nums"
            >
              {pilot.missionCount} mission{pilot.missionCount === 1 ? "" : "s"}
            </Link>
          ) : (
            <span className="text-[0.9rem] text-ink-soft">No missions</span>
          )}
          {isAdmin && (
            <div className="flex gap-2">
              {!archived && (
                <Button variant="secondary" className="h-9 px-4" onClick={() => setEditing(true)}>
                  Rename
                </Button>
              )}
              <Button
                variant="secondary"
                className="h-9 px-4"
                onClick={() => void change(() => api.updatePilot(pilot.id, { archived: !archived }))}
              >
                {archived ? "Restore" : "Archive"}
              </Button>
            </div>
          )}
        </>
      )}
    </li>
  );
}
